import api from "@/api";
import { all } from "axios";
import { defineStore } from "pinia";

const endPoint = "/v2/wonde/schools";
const qsParams = "?per_page=200";

interface WondeApiState {
  selectedSchoolId: string | null;
}

interface Teacher {
  id: string;
  forename: string;
  surname: string;
  classes?: { data: Array<any> };
  groups?: { data: Array<any> };
}

interface ClassGroup {
  grade: number;
  gradeClass: string;
  name: string;
  id: string;
}

export const useWondeApiStore = defineStore({
  id: "wondeApi",
  state: (): WondeApiState => ({
    selectedSchoolId: null,
  }),
  getters: {
    selectedSchoolId: (state): string | null => {
      return state.selectedSchoolId;
    },
  },
  actions: {
    async getSchools() {
      try {
        const response = await api.get(`${endPoint}${qsParams}`);
        return response.data;
      } catch (error) {
        console.error("Error fetching schools:", error);
        throw error;
      }
    },
    async getSchoolEntities(schoolId: string) {
      try {
        const response = await api.get(`${endPoint}/${schoolId}/sync-config`);
        const filtered = this.filterClassGroups(
          response.data.classes,
          response.data.groups
        );
        return {
          teachers: response.data.teachers,
          classGroups: filtered,
        };
      } catch (error) {
        console.error("Error fetching school entities:", error);
        throw error;
      }
    },
    async getClassGroups(schoolId: string): Promise<ClassGroup[]> {
      try {
        const response = await Promise.all([
          this.getAllClasses(schoolId),
          this.getAllGroups(schoolId),
        ]);
        const classes = response[0] ? response[0] : [];
        const groups = response[1] ? response[1] : [];
        const filtered = this.filterClassGroups(classes, groups);
        return filtered;
      } catch (error) {
        console.error("Error fetching class groups:", error);
        throw error;
      }
    },
    async getAllClasses(schoolId: string) {
      let page = 1;
      let allClasses: any[] = [];
      let response;
      do {
        response = await api.get(
          `${endPoint}/${schoolId}/classes${qsParams}&page=${page}`
        );
        allClasses = [...allClasses, ...response.data.data];
        page++;
      } while (response.data?.meta?.pagination?.more === true);
      return allClasses;
    },
    async getAllGroups(schoolId: string) {
      let page = 1;
      let allGroups: any[] = [];
      let response;
      do {
        response = await api.get(
          `${endPoint}/${schoolId}/groups${qsParams}&page=${page}`
        );
        allGroups = [...allGroups, ...response.data.data];
        page++;
      } while (response.data?.meta?.pagination?.more === true);
      return allGroups;
    },

    async getTeachers(schoolId: string): Promise<Teacher[]> {
      try {
        const includes =
          "include=classes,groups,classes.subject,contact_details";
        const response = await api.get(
          `${endPoint}/${schoolId}/employees${qsParams}&${includes}`
        );
        const teachers: Teacher[] = response.data.data;
        const filteredTeachers = teachers.filter(
          (teacher) =>
            (teacher.classes?.data && teacher.classes?.data.length > 0) ||
            (teacher.groups?.data && teacher.groups?.data.length > 0)
        );
        return filteredTeachers;
      } catch (error) {
        console.error("Error fetching teachers:", error);
        throw error;
      }
    },
    async getStudents(schoolId: string) {
      try {
        const response = await api.get(
          `${endPoint}/${schoolId}/students${qsParams}`
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching students:", error);
        throw error;
      }
    },
    async createSchool(formData: any) {
      try {
        const response = await api.post(
          "/v2/organisation/school/new",
          formData
        );
        return response.data;
      } catch (error) {
        throw new Error("Failed to create school.");
      }
    },
    async triggerJobSync(schoolId: string) {
      try {
        const response = await api.get(`/v2/wonde/sync?schoolId=${schoolId}`);
        if (response.status === 200) return true;
      } catch (error) {
        console.error("Error triggering Wonde Sync Jobs.", error);
        return false;
      }
      return false;
    },
    /**
     * Schools already imported from Wonde and exist in DB
     */
    async getExistingSchools() {
      try {
        const response = await api.get("/v2/wonde/schools/existing/entries");
        return response.data;
      } catch (error) {
        console.log("getExistingSchools error", error);
        return false;
      }
    },
    /**
     * Filter classes and groups from Wonde API
     * @param classes array
     * @param groups array
     * @returns object
     */
    filterClassGroups(classes: Array<any>, groups: Array<any>): ClassGroup[] {
      const allClassGroups = [...classes, ...groups];
      const filteredData: ClassGroup[] = [];
      let grade: number;
      let gradeClass: string;

      console.log('CLASSES', allClassGroups);

      for (const item of allClassGroups) {
        // Find the first occurrence of a number 1-12 (grade) anywhere in the name
        const match = item.name.match(/(1[0-2]|[1-9])/);
        if (!match) continue;
        grade = parseInt(match[0], 10);
        const start = match.index ?? 0;
        const end = start + match[0].length;
        // Remove only the matched grade digits from the name to get the class part
        gradeClass = (item.name.slice(0, start) + item.name.slice(end)).trim();
        // fallback to original name if removal produced empty string
        if (!gradeClass) gradeClass = item.name;
        const exists = filteredData.find(
          (d) =>
            d.grade === grade &&
            d.gradeClass === gradeClass
        );
        if (!exists) {
          filteredData.push({ grade, gradeClass, id: item.id, name: item.name });
        }
       }
       return filteredData;
     },
  },
});
